import { Card, Counter, Flex, Text } from "../../components";
import { DownOutlined } from "@ant-design/icons";
import { CartItem } from "../../types/item";
import { useMenu } from "../../hooks";
import { optionAmount } from "../../utils/menu";
import { Tools } from "../../utils";
import { useState } from "react";
interface ItemProps extends CartItem {
  onAdd?: () => void;
  onRemove?: () => void;
  onDelete?: () => void;
  closeCart?: () => void;
  editable?: boolean;
}
const Item = ({
  quantity,
  favorite,
  price,
  totalPrice,
  editable = true,
  onAdd,
  onRemove,
  onDelete,
  closeCart,
  ...item
}: ItemProps) => {
  const { openItem } = useMenu();
  const [addonsOpen, setAddonsOpen] = useState(false);
  const fromOptions = (item.options ?? [])
    .map((option) => ({
      uuid: option.uuid,
      name: option.name,
      chosen: option.subOptions.filter((subOption) => subOption.checked),
    }))
    .filter((option) => option.chosen.length);
  const fromChoices = fromOptions.length
    ? []
    : Object.entries(
        (item.choices ?? []).reduce<
          Record<string, NonNullable<typeof item.choices>>
        >((groups, choice) => {
          const name = choice.group || "Choices";
          groups[name] = [...(groups[name] ?? []), choice];
          return groups;
        }, {}),
      ).map(([name, choices]) => ({
        uuid: name,
        name,
        chosen: (choices ?? []).map((choice) => ({
          uuid: choice.uuid,
          name: choice.name,
          price: choice.price,
          extraPrice: choice.price,
        })),
      }));
  const addons = fromOptions.length ? fromOptions : fromChoices;

  return (
    <Flex className="group" align="flex-start">
      <button
        type="button"
        aria-label={item.name}
        onClick={() => item.isAvailable && openItem(item.uuid)}
        className="h-[8rem] w-[8rem] min-w-[8rem] max-w-[8rem] shrink-0 cursor-pointer overflow-hidden rounded-[1rem] border-0 p-0"
      >
        {!!item.imageUrl && (
          <img
            alt=""
            src={Tools.mediaUrl(item.imageUrl)}
            className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:[transform:scale(1.2)] motion-reduce:transition-none"
          />
        )}
      </button>
      <div className="min-w-0 flex-1 p-15-l">
        {editable ? (
          <Flex align="center" gap="1rem">
            <div className="min-w-0 flex-1">
              <Text level={5} children={item.name} />
              <Text
                weight={600}
                className="m-5-t"
                children={Tools.toCurrency(totalPrice)}
              />
            </div>
            <Counter value={quantity} onAdd={onAdd} onRemove={onRemove} />
          </Flex>
        ) : (
          <Flex justify="space-between" align="center" gap="2rem">
            <Text
              level={5}
              className="min-w-0"
              children={`${quantity}x ${item.name}`}
            />
            <Text
              weight={600}
              className="shrink-0"
              children={Tools.toCurrency(totalPrice)}
            />
          </Flex>
        )}
        {!!addons.length && (
          <button
            type="button"
            aria-expanded={addonsOpen}
            onClick={() => setAddonsOpen((open) => !open)}
            className="m-5-t inline-flex cursor-pointer items-center gap-[0.4rem] border-0 bg-transparent p-0"
          >
            <Text size="1.4rem" type="secondary" children="View add on" />
            <DownOutlined
              className={`text-[1.2rem] text-black/45 transition-transform duration-200 ${
                addonsOpen
                  ? "[transform:rotate(180deg)]"
                  : "[transform:rotate(0deg)]"
              }`}
            />
          </button>
        )}
        {addonsOpen && (
          <Flex vertical gap=".5rem" className="m-5-t">
            {addons.map((option) => (
              <div key={option.uuid || option.name}>
                <Text weight={600} children={option.name} />
                {option.chosen.map((subOption) => (
                  <Flex
                    key={subOption.uuid || subOption.name}
                    justify="space-between"
                    align="center"
                  >
                    <Text children={`1x ${subOption.name}`} />
                    {!!optionAmount(subOption) && (
                      <Text
                        children={Tools.toCurrency(optionAmount(subOption))}
                      />
                    )}
                  </Flex>
                ))}
              </div>
            ))}
          </Flex>
        )}
      </div>
    </Flex>
  );
};

export default Item;
